import cx from 'classnames';
import React, {ChangeEvent, KeyboardEvent} from 'react';

import {Label, LabelTheme} from './Label';

interface IProps {
    label?: string;
    value: string;
    onChange?: (newValue: string) => void;
    onEnter?: () => void;
    onBlur?: () => void;
    type?: 'text' | 'password' | 'email';
    placeholder?: string;
    helpText?: string;
    error?: string;
    loading?: boolean;
    icon?: React.ReactNode;
    className?: string;
    disabled?: boolean;
    isSearch?: boolean;
    applyBorder?: boolean;
    labelTheme?: LabelTheme;
}

export function TextInput(props: IProps) {
    const {
        label,
        value,
        onChange,
        onEnter,
        onBlur,
        type = 'text',
        placeholder,
        helpText,
        error,
        className,
        disabled,
        isSearch,
        applyBorder,
        labelTheme,
    } = props;

    const inErrorState = error !== undefined;
    const hasErrorMessage = !!error;

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        if (!onChange) {
            return;
        }

        onChange(event.target.value);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (onEnter && event.key === 'Enter') {
            onEnter();
        }
    }

    const iconNode = getIconFromProps(props);

    return (
        <div className={className}>
            {label && <Label error={inErrorState} theme={labelTheme}>
                {label}
            </Label>}
            <div className={cx(
                isSearch ? "text-left flex items-center w-full border-2 rounded border-15" :
                    "mt-2 relative shadow-sm"
            )}>
                {isSearch && <i className="fas fa-search text-40 ml-2"/>}

                <input
                    type={type}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cx(
                        'bg-transparent',
                        isSearch ? ' flex-1 text-40 outline-none ml-3 h-10' :
                            applyBorder ? 'border-15 h-12 text-xs p-2 outline-none w-full rounded border-2 text-london-air-white' :
                            'rounded border-40 outline-none p-2 w-full sm:text-sm sm:leading-5 text-slightly-off-white border-2',
                        disabled &&
                        'border-40 border-2 text-40 placeholder-40 cursor-not-allowed',
                        inErrorState &&
                        !disabled &&
                        'border-2 border-warning-red text-warning-red placeholder-warning-red focus:border-warning-red focus:ring-warning-red pr-30',
                    )}
                />

                {/* Error icon */}
                {iconNode && (
                    <div className="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none">
                        {iconNode}
                    </div>
                )}
            </div>
            {helpText && <p className="mt-2 text-14 text-40">{helpText}</p>}
            {hasErrorMessage && <p className="mt-2 text-14 text-darker-red xl:text-warning-red">{error}</p>}
        </div>
    );
}

function getIconFromProps({error, loading, icon}: IProps) {
    if (error) {
        return (
            <svg
                className="h-20 w-20 text-warning-red"
                fill="currentColor"
                viewBox="0 0 20 20"
            >
                <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }

    if (loading) {
        return <i className="fas fa-circle-notch fa-spin text-18 text-smurfs-gold"/>;
    }

    if (icon) {
        return icon;
    }

    return null;
}
